import { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    MenuItem,
    Grid,
    IconButton,
    InputAdornment,
    Drawer,
    Divider,
    Button,
    Badge
} from '@mui/material';
import PropTypes from 'prop-types';
import { Search as SearchIcon, LocalShipping, CheckCircle, AccessTime, LocationOn, Settings } from '@mui/icons-material';
import { format } from 'date-fns';
import { useAdminAllOrders, useUpdateOrder } from 'hooks/useOrder';
import { useAdminAllRiders } from 'hooks/useRiders';
import { useDebounce } from 'hooks/useDebounce'; // Or a simple local state
import FilterDrawer from 'components/common/FilterDrawer';
import PermissionGuard from 'ui-component/PermissionGuard';
import { FilterList } from '@mui/icons-material';

export default function OrderList() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [search, setSearch] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({}); // Store active filters

    const debouncedSearch = useDebounce(search, 500); // Wait 500ms after typing stops

    const {
        data: orderRes,
        isLoading,
        refetch
    } = useAdminAllOrders({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        search: debouncedSearch || undefined, // Use debounced value here
        ...filters
    });

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const filterConfig = [
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' },
        {
            name: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
                { label: 'Newest', value: 'createdAt' },
                { label: 'Amount', value: 'totalAmount' }
            ]
        },
        {
            name: 'sortOrder',
            label: 'Sort Order',
            type: 'select',
            options: [
                { label: 'Descending', value: 'desc' },
                { label: 'Ascending', value: 'asc' }
            ]
        }
    ];

    const orders = orderRes?.data || [];
    const total = orderRes?.total || 0;
    const skeletonRows = Array.from({ length: 8 });

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    const activeFiltersCount = Object.keys(filters).filter((key) => filters[key]).length;

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Typography variant="h4" fontWeight={700} mb={4}>
                Logistics Control
            </Typography>

            {/* 1. Stat Cards (Matches TransactionLog Style) */}
            <Grid container spacing={3} mb={4}>
                <OrderStatCard title="Total Orders" value={total} color="#1976d2" icon={<LocalShipping color="primary" />} />
                <OrderStatCard
                    title="Active"
                    value={orders.filter((o) => o.status === 'pending' || o.status === 'in-progress').length}
                    color="#ef6c00"
                    icon={<AccessTime color="warning" />}
                />
                <OrderStatCard
                    title="Completed"
                    value={orders.filter((o) => o.status === 'completed').length}
                    color="#2e7d32"
                    icon={<CheckCircle color="success" />}
                />
            </Grid>

            <Paper sx={{ width: '100%', overflow: 'hidden', padding: 2, borderRadius: 2 }}>
                {/* 2. Search & Filters (Matches TransactionLog Style) */}
                <Stack direction="row" spacing={2} mb={3} alignItems="center">
                    <TextField
                        placeholder="Search ID, Customer, or Rider..."
                        size="small"
                        value={search}
                        onChange={handleSearchChange}
                        sx={{ width: '350px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(0);
                        }}
                        size="small"
                        sx={{ maxWidth: 400 }}
                    >
                        <MenuItem value="ALL">All Statuses</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="in-progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Badge badgeContent={activeFiltersCount} color="primary">
                            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterOpen(true)}>
                                Filters
                            </Button>
                        </Badge>
                        {activeFiltersCount > 0 && (
                            <Button variant="text" onClick={() => setFilters({})} color="error" size="small">
                                Reset
                            </Button>
                        )}
                    </Stack>
                    <Box sx={{ flexGrow: 1 }} />
                </Stack>

                {/* 3. Table Layout */}
                <TableContainer sx={{ maxHeight: 650 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Parties</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    Action
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                skeletonRows.map((_, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell colSpan={6}>
                                            <Skeleton height={40} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : orders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        No orders found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                orders.map((order) => (
                                    <TableRow hover key={order.id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700}>
                                                #{order.orderId || order.id.slice(-6).toUpperCase()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {format(new Date(order.createdAt), 'MMM dd, hh:mm a')}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {order.customer?.firstName} (Cust.)
                                                </Typography>
                                                <Typography variant="caption" color="primary.main">
                                                    {order.rider ? `🚴 ${order.rider.firstName}` : '⚠️ No Rider'}
                                                </Typography>
                                            </Stack>
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 220 }}>
                                            <Typography variant="caption" noWrap display="block">
                                                <LocationOn sx={{ fontSize: 10, color: 'success.main' }} /> {order.pickupAddress}
                                            </Typography>
                                            <Typography variant="caption" noWrap display="block">
                                                <LocationOn sx={{ fontSize: 10, color: 'error.main' }} /> {order.deliveryAddress}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700}>
                                                ₦{order.totalAmount?.toLocaleString()}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={order.status}
                                                size="small"
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    fontWeight: 700,
                                                    fontSize: '0.65rem',
                                                    bgcolor: order.status === 'completed' ? '#e8f5e9' : '#fff3e0',
                                                    color: order.status === 'completed' ? '#2e7d32' : '#ef6c00'
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="right">
                                            <IconButton color="primary" onClick={() => setSelectedOrder(order)}>
                                                <Settings fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* 4. Order Detail & Control Center Drawer */}
            <OrderControlDrawer open={!!selectedOrder} order={selectedOrder} onClose={() => setSelectedOrder(null)} onUpdate={refetch} />

            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                initialFilters={filters}
                config={filterConfig}
            />
        </Box>
    );
}

// --- Sub-Component: Stat Card ---
function OrderStatCard({ title, value, icon, color }) {
    return (
        <Grid item xs={12} sm={4}>
            <Paper
                sx={{
                    p: 2.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {title.toUpperCase()}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                        {value}
                    </Typography>
                </Box>
                <Avatar sx={{ bgcolor: `${color}15`, p: 1 }}>{icon}</Avatar>
            </Paper>
        </Grid>
    );
}

OrderStatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    icon: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired
};

// --- Sub-Component: Order Control Drawer ---
function OrderControlDrawer({ open, order, onClose }) {
    if (!order) return null;

    /*
    const handleAction = (payload) => {
        updateOrder(
            { orderId: order.id, ...payload },
            {
                onSuccess: () => {
                    // onUpdate(); // No longer strictly needed if using invalidateQueries in hook
                    onClose();
                    setAssignMode(false);
                },
                onError: (error) => {
                    console.error('Update failed:', error);
                    // You should ideally trigger a snackbar here
                }
            }
        );
    };
    */

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 400, p: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                    Order Control Center
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Internal ID: {order.id}
                </Typography>
                <Divider sx={{ my: 2 }} />

                {/* MVP Admin Action Area */}
                {/* <Box sx={{ bgcolor: '#fff4e5', p: 2, borderRadius: 2, mb: 3 }}>
                    <Typography variant="subtitle2" color="#663c00" fontWeight={700} mb={2}>
                        Administrative Actions
                    </Typography>

                    {!assignMode ? (
                        <Stack spacing={1}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="warning"
                                startIcon={<DirectionsBike />}
                                onClick={() => setAssignMode(true)}
                            >
                                {order.rider ? 'Reassign Rider' : 'Assign Rider'}
                            </Button>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    size="small"
                                    onClick={() => handleAction({ status: 'completed' })}
                                >
                                    Complete
                                </Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => handleAction({ status: 'cancelled' })}
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack spacing={2}>
                            <TextField
                                select
                                label="Select Rider"
                                size="small"
                                fullWidth
                                value={riderId}
                                onChange={(e) => setRiderId(e.target.value)}
                            >
                                {riderRes?.data?.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>
                                        {r.firstName} {r.lastName}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={() => setAssignMode(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disabled={isUpdating}
                                    onClick={() => handleAction({ status: 'completed' })}
                                >
                                    {isUpdating ? <CircularProgress size={20} /> : 'Complete'}
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Box> */}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="primary" gutterBottom>
                    Logistics Details
                </Typography>
                <DetailItem label="Pickup" value={order.pickupAddress} />
                <DetailItem label="Drop-off" value={order.deliveryAddress} />
                <DetailItem label="Customer" value={`${order.customer?.firstName} ${order.customer?.lastName}`} />
                <DetailItem label="Amount" value={`₦${order.totalAmount?.toLocaleString()}`} />

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={2} mb={3}>
                    <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                            TYPE
                        </Typography>
                        <Chip
                            label={order.orderType || 'Product'}
                            size="small"
                            variant="outlined"
                            sx={{
                                display: 'block',
                                mt: 0.5,
                                textTransform: 'uppercase',
                                fontWeight: 700
                            }}
                        />
                    </Box>
                    <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                            PAYMENT
                        </Typography>
                        <Chip
                            label={order.paymentCompleted ? 'PAID' : 'PENDING'}
                            size="small"
                            color={order.paymentCompleted ? 'success' : 'warning'}
                            sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}
                        />
                    </Box>
                </Stack>

                <Button
                    fullWidth
                    variant="contained"
                    href={`/admin/orders/${order.id}`}
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/admin/orders/${order.id}`;
                    }}
                    sx={{ mt: 2, borderRadius: 2 }}
                >
                    View Full Details
                </Button>
            </Box>
        </Drawer>
    );
}

OrderControlDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    order: PropTypes.object,
    onClose: PropTypes.func.isRequired
};

function DetailItem({ label, value }) {
    return (
        <Box mb={1.5}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );
}

DetailItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
